'use client';

import { useEffect, useState, RefObject } from 'react';

interface ScrollState {
  isScrolledDown: boolean;
  isScrollable: boolean;
  scrollDown: () => void;
}

export const useScrollDown = (
  ref: RefObject<HTMLElement | null>,
  offset: number = 0,
): ScrollState => {
  const [isScrolledDown, setIsScrolledDown] = useState<boolean>(false);
  const [isScrollable, setIsScrollable] = useState<boolean>(false);

  const scrollDown = (): void => {
    if (!ref.current) return;
    ref.current.scrollTo({
      top: ref.current.scrollHeight,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const handleScroll = (): void => {
      const hasScroll = element.scrollHeight > element.clientHeight;
      const bottomPosition = Math.abs(
        element.scrollHeight - element.scrollTop - element.clientHeight,
      );

      setIsScrollable(hasScroll);
      setIsScrolledDown(bottomPosition <= offset);
    };

    element.addEventListener('scroll', handleScroll);
    handleScroll();

    const resizeObserver = new ResizeObserver(handleScroll);
    resizeObserver.observe(element);

    return () => {
      element.removeEventListener('scroll', handleScroll);
      resizeObserver.disconnect();
    };
  }, [ref, offset]);

  return { isScrolledDown, isScrollable, scrollDown };
};
